import { BrokerCallCard, BrokerCallStatus, VoiceTranscriptRole } from '@numeo/types'
import { formatUsdWhole, formatUsPhone } from '@numeo/types-core'
import { useState } from 'react'
import { ChevronDown, ChevronUp, PhoneOutgoing } from 'lucide-react'
import { Badge, BadgeType } from '@/components/ds2/Badge'
import { callDuration } from '../callDuration'
import { RecordingPlayer } from './RecordingPlayer'

// The thread reads the card as stored, written at the dial and again at the result: in between it stays
// `starting` whether the call is ringing, talking or already failed, so that status can only say "In progress".
const STATUS_BADGE: Record<BrokerCallStatus, { label: string; type: BadgeType }> = {
    [BrokerCallStatus.Starting]: { label: 'In progress', type: 'accent' },
    [BrokerCallStatus.Connected]: { label: 'On the call', type: 'accent' },
    [BrokerCallStatus.Ended]: { label: 'Ended', type: 'success' },
    [BrokerCallStatus.Failed]: { label: 'Failed', type: 'danger' },
    [BrokerCallStatus.Cancelled]: { label: 'Cancelled', type: 'default' },
}

// Calls with more turns than this start collapsed; short exchanges render open, with no toggle.
const SHORT_TRANSCRIPT_THRESHOLD = 3

/** The dialed number, with the extension the card carries after `.ext`. */
function phoneLabel(phoneNumber: string): string {
    const [number, extension] = phoneNumber.split('.ext')
    return extension ? `${formatUsPhone(number)} ext. ${extension}` : formatUsPhone(number)
}

/** What the call collected: the rate and the load facts the broker confirmed. */
function collectedFacts(card: BrokerCallCard): string[] {
    const details = card.details
    if (!details) return []
    return [
        details.rateUsd !== undefined ? `Rate ${formatUsdWhole(details.rateUsd)}` : '',
        details.weight ?? '',
        details.commodity ?? '',
        details.specialRequirements ?? '',
    ].filter(Boolean)
}

/** A broker call the agent placed → a call card (parity with the mobile), never the raw JSON. */
export function BrokerCallLogCard({ card, tabId }: { card: BrokerCallCard; tabId: string }) {
    const isLong = card.transcript.length > SHORT_TRANSCRIPT_THRESHOLD
    const [expanded, setExpanded] = useState(!isLong)
    const status = STATUS_BADGE[card.status]
    const duration = callDuration(card.startedAt, card.endedAt)
    const outcome = card.resultText?.split('\n')[0]?.trim()
    const facts = collectedFacts(card)

    return (
        <div className="flex justify-start">
            <div className="w-full max-w-[85%] rounded-2xl bg-surface p-3 shadow-sm ring-1 ring-border">
                <div className="flex items-start gap-2">
                    <PhoneOutgoing className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="text-[13px] font-semibold text-foreground">{card.brokerName ?? card.label}</span>
                            <Badge variant="soft" size="sm" type={status.type} label={status.label} />
                        </div>
                        {card.brokerName && card.route && <div className="truncate text-xs text-foreground-muted">{card.route}</div>}
                        <div className="text-[11px] text-foreground-muted">
                            AI call · {phoneLabel(card.phoneNumber)}
                            {duration ? ` · ${duration}` : ''}
                        </div>
                    </div>
                </div>
                {outcome && <p className="mt-2 text-sm text-foreground">{outcome}</p>}
                {facts.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                        {facts.map((fact) => (
                            <Badge key={fact} variant="secondary" size="sm" type="default" label={fact} />
                        ))}
                    </div>
                )}
                <p className="mt-2 line-clamp-3 text-xs text-foreground-muted" title={card.purpose}>
                    <span className="font-medium">Brief: </span>
                    {card.purpose}
                </p>
                {card.recordingUrl && (
                    <div className="mt-2">
                        <RecordingPlayer key={`${tabId}:${card.requestId}`} tabId={tabId} messageId={card.requestId} url={card.recordingUrl} />
                    </div>
                )}
                {isLong && (
                    <button type="button" onClick={() => setExpanded((v) => !v)} className="mt-2 flex items-center gap-0.5 text-[11px] font-medium text-accent">
                        {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {expanded ? 'Hide transcript' : `Show transcript (${card.transcript.length})`}
                    </button>
                )}
                {expanded && card.transcript.length > 0 && (
                    <div className="mt-1.5 max-h-72 space-y-1 overflow-y-auto text-sm">
                        {card.transcript.map((turn, i) => (
                            <div key={i} className={turn.role === VoiceTranscriptRole.ASSISTANT ? 'text-foreground-muted' : 'text-foreground'}>
                                <span className="font-medium">{turn.role === VoiceTranscriptRole.ASSISTANT ? 'AI' : 'Broker'}:</span> {turn.content}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}
